import { useEffect, useState } from 'react';
import {
  Checkbox,
  IconUp,
  IconDown,
  Filters,
  useFilters,
  FilterTag,
  IconDownload,
  IconTrash,
} from '@ui/src';
import useSelectedTags from '@ui/src/components/table/hooks/use-selected-tags';
import { SchemaFilterType } from '@ui/src/components/table/types';
import useGetAccountsAutocompleteList from '@transactions/hoc/cash-collection/use-accounts-autocomplete';
import { useAppSelector } from '@store';
import { FilterPresetContentTypes } from './types';

import cn from './profile.module.css';

const FilterPresetContent = ({
  item,
  selectedPresets,
  setSelectedPresets,
  columnsLabels,
  filtersSchema,
  filtersPresetKey,
  userId,
  setFiltersPresets,
}: FilterPresetContentTypes) => {
  const [expand, setExpand] = useState(false);
  const [values, setValues] = useState<Filters>({});

  const {
    administrationDictionaries: { activeCurrencies: currencies, countries },
    settings: {
      paymentTypes: { paymentTypes },
      profile: { pspPartnerAccounts },
      details: { rateSources },
    },
  } = useAppSelector((state) => state);

  const { selectedOptionsTags } = useSelectedTags(values, columnsLabels);

  const { autocompleteList: recipientAccounts, getAutocompleteListAction } =
    useGetAccountsAutocompleteList('&isClientAccount=true');

  const {
    autocompleteList: senderAccounts,
    getAutocompleteListAction: getSenderAutocompleteListAction,
  } = useGetAccountsAutocompleteList('&isClientAccount=true');

  const { filtersComponent, setValues: setFiltersValues } = useFilters({
    filtersSchema,
    filters: {},
    onFilterChange: () => {},
  });

  const handleRemoveFilterTag = (fieldName: string, value: string) => {
    const nextFilters = { ...values };

    Object.entries(values).forEach(([key]) => {
      if (key === fieldName) {
        if (nextFilters[key].type !== SchemaFilterType.DATE_RANGE) {
          nextFilters[key] = {
            value: values[key]?.value?.filter(
              (itemValue) => itemValue !== value,
            ),
            type: nextFilters[key].type,
          };
        } else {
          delete nextFilters[key];
        }
      }
    });

    setValues(nextFilters);

    const filtersPresetsJSON = localStorage.getItem(filtersPresetKey);

    if (filtersPresetsJSON) {
      const parsed = JSON.parse(filtersPresetsJSON);

      if (parsed) {
        const nextParsedFiltersPresets = parsed?.map((preset: any) => {
          const result = { ...preset };

          if (result.id === item?.id) {
            result.value = nextFilters;
          }

          return result;
        });

        const currentUserPresets = nextParsedFiltersPresets?.filter(
          (preset: any) => preset.employeeId === userId,
        );

        setFiltersPresets(currentUserPresets);

        localStorage.setItem(
          filtersPresetKey,
          JSON.stringify(nextParsedFiltersPresets),
        );
      }
    }
  };

  const onDownloadPreset = () => {
    const fileContent = {
      key: filtersPresetKey,
      values: [item],
    };

    const blob = new Blob([JSON.stringify(fileContent)], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    const savedFiltersName = filtersPresetKey
      ?.split('-')
      ?.map((key) => `${key.charAt(0).toUpperCase()}${key.slice(1)}`)
      ?.join(' ');

    a.href = url;
    a.download = `${savedFiltersName} ${item?.name} preset`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onDeleteFilterPreset = () => {
    const savedFiltersJSON = localStorage.getItem(filtersPresetKey);

    if (savedFiltersJSON) {
      const parsed: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
        employeeId: string;
      }[] = JSON.parse(savedFiltersJSON);

      if (parsed) {
        const nextSavedFilters = parsed
          ?.filter((filterItem) => filterItem.id !== item?.id)
          ?.map((filterItem, index) => ({
            ...filterItem,
            id: `${filtersPresetKey}-${index + 1}`,
          }));

        const currentUserSavedFilters = nextSavedFilters?.filter(
          (filterItem) => filterItem.employeeId === userId,
        );

        setFiltersPresets(currentUserSavedFilters);

        if (nextSavedFilters?.length) {
          localStorage.setItem(
            filtersPresetKey,
            JSON.stringify(nextSavedFilters),
          );
        } else {
          localStorage.removeItem(filtersPresetKey);
        }
      }
    }
  };

  useEffect(() => {
    if (currencies) {
      setFiltersValues(
        'currencyId',
        currencies?.map((currency) => ({
          name: currency.name,
          value: currency.id,
        })),
      );
      setFiltersValues(
        'exchangeCurrencyFromId',
        currencies?.map((currency) => ({
          name: currency.name,
          value: currency.id,
        })),
      );
      setFiltersValues(
        'exchangeCurrencyToId',
        currencies?.map((currency) => ({
          name: currency.name,
          value: currency.id,
        })),
      );
    }

    if (recipientAccounts) {
      setFiltersValues('recepientAccountId', recipientAccounts);
      setFiltersValues('recipientAccountNumber', recipientAccounts);
    }

    if (senderAccounts) {
      setFiltersValues('senderAccountNumber', senderAccounts);
    }

    if (paymentTypes) {
      setFiltersValues(
        'paymentType',
        paymentTypes?.map((type) => ({
          name: type.name,
          value: type.paymentType,
        })) || [],
      );
    }

    if (countries) {
      setFiltersValues('remitterCountry', countries);
      setFiltersValues('benificiaryCountry', countries);
    }

    if (pspPartnerAccounts) {
      setFiltersValues('pspPartnerAccountNumber', pspPartnerAccounts);
    }

    if (rateSources) {
      setFiltersValues(
        'exchangeRateSourceId',
        rateSources?.map((rateSource) => ({
          name: rateSource.name,
          value: rateSource.id,
        })),
      );
    }
  }, [
    currencies,
    recipientAccounts,
    senderAccounts,
    paymentTypes,
    countries,
    pspPartnerAccounts,
    rateSources,
  ]);

  useEffect(() => {
    if (
      values?.recepientAccountId &&
      recipientAccounts &&
      !recipientAccounts?.find(
        (account) => account.value === values?.recepientAccountId.value[0],
      )
    ) {
      getAutocompleteListAction(
        '',
        `&ids=${values?.recepientAccountId.value[0]}`,
      );
    }

    if (
      values?.senderAccountId &&
      senderAccounts &&
      !senderAccounts?.find(
        (account) => account.value === values?.senderAccountId.value[0],
      )
    ) {
      getSenderAutocompleteListAction(
        '',
        `&ids=${values?.senderAccountId.value[0]}`,
      );
    }
  }, [values, recipientAccounts, senderAccounts]);

  useEffect(() => {
    if (item) {
      setValues(item.value);
    }
  }, [item]);

  return (
    <div className={cn.filtersPresetListItemWrapper}>
      <div className={cn.filtersPresetListItem}>
        <button
          className={cn.expandButton}
          type="button"
          aria-label="Expand Button"
          onClick={() => setExpand(!expand)}
        />
        <div className={cn.filtersPresetListItemLeft}>
          <Checkbox
            name={`preset-${item?.id}`}
            title={item?.name}
            type="checkbox"
            labelClassName={cn.filtersPresetListItemLeftCheckbox}
            checked={Boolean(
              selectedPresets?.find((selected) => item?.id === selected.id),
            )}
            onChange={() => {
              let nextSelectedPresets = (
                [] as {
                  id: string;
                  name: string;
                  value: Record<string, any>;
                  default?: boolean;
                  employeeId: string;
                }[]
              ).concat(selectedPresets);

              if (
                selectedPresets?.find((selected) => item?.id === selected.id)
              ) {
                nextSelectedPresets = nextSelectedPresets?.filter(
                  (selected) => item?.id !== selected.id,
                );
              } else {
                nextSelectedPresets.push(
                  item as {
                    id: string;
                    name: string;
                    value: Record<string, any>;
                    default?: boolean;
                    employeeId: string;
                  },
                );
              }

              setSelectedPresets(nextSelectedPresets);
            }}
          />
          {expand ? (
            <IconUp className={cn.buttonIcon} />
          ) : (
            <IconDown className={cn.buttonIcon} />
          )}
        </div>
        <div className={cn.filtersPresetListItemRight}>
          <button
            type="button"
            className={cn.downloadButton}
            aria-label="Download Button"
            onClick={onDownloadPreset}
          >
            <IconDownload />
          </button>
          <button
            type="button"
            className={`${cn.downloadButton} ${cn.deleteButton}`}
            aria-label="Download Button"
            onClick={onDeleteFilterPreset}
          >
            <IconTrash />
          </button>
        </div>
      </div>
      {expand && (
        <div className={cn.filtersValuesList}>
          {selectedOptionsTags?.map((filterItem) => {
            let name = filterItem.value;

            if (filtersComponent?.list) {
              const currentList = filtersComponent?.list[filterItem.key];

              if (currentList) {
                const currentItem = currentList.find(
                  (listItem) =>
                    listItem.value === filterItem.value ||
                    listItem.suffix === filterItem.value,
                );

                if (currentItem) {
                  name = currentItem.name;

                  if (currentItem.suffix) {
                    name = `${name} (${currentItem.suffix})`;
                  }
                }
              }
            }

            return (
              <div
                key={`${filterItem.key}-${filterItem.value}`}
                className={cn.filterItem}
              >
                <FilterTag
                  type="round"
                  title={
                    <>
                      <span>{`${filterItem?.label as string}: `}</span>
                      <span>{name}</span>
                    </>
                  }
                  onClick={() =>
                    handleRemoveFilterTag(filterItem.key, filterItem.value)
                  }
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FilterPresetContent;
